import { Button } from '@repo/ui/src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/src/components/ui/card';
import { Input } from '@repo/ui/src/components/ui/input';
import { Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchApi } from '../../../lib/api';
import { useUpdateProfile } from '../../../hooks/useProfile';
import { useAuthStore } from '../../../stores/authStore';
import { SettingRow } from './SettingRow';

/**
 * Account card: name and email. Read-by-default rows — each value
 * inline-edits in place rather than showing a permanently-open form.
 * (Password lives in its own card — see PasswordCard.tsx.)
 */
export function PersonalCard() {
  const { t } = useTranslation('settings');
  const user = useAuthStore((s) => s.user);
  const [resendState, setResendState] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle');
  const updateProfile = useUpdateProfile();

  const currentFullName = user?.fullName ?? '';

  // Name edit mode. The draft is seeded from the store only at the moment
  // "Sửa" is clicked — no effect-based sync is needed since the input isn't
  // mounted until then.
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(currentFullName);

  const startEditingName = () => {
    setName(currentFullName);
    setEditingName(true);
  };

  const trimmedName = name.trim();
  const canSaveName =
    trimmedName.length > 0 && trimmedName !== currentFullName && !updateProfile.isPending;

  const initials = user?.fullName
    ? user.fullName
        .split(' ')
        .map((w) => w[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : '??';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('profile.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Name — read shows the avatar + name, edit swaps in an input */}
        <SettingRow
          label={t('profile.fullName')}
          action={
            !editingName && (
              <Button variant="outline" size="sm" onClick={startEditingName}>
                {t('profile.edit')}
              </Button>
            )
          }
        >
          {editingName ? (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="max-w-xs"
                autoFocus
              />
              <Button
                size="sm"
                disabled={!canSaveName}
                onClick={() =>
                  updateProfile.mutate(
                    { fullName: trimmedName },
                    { onSuccess: () => setEditingName(false) },
                  )
                }
              >
                {updateProfile.isPending ? t('profile.saving') : t('profile.save')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditingName(false)}>
                {t('profile.cancel')}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-400 to-emerald-500 text-xs font-bold text-white">
                {initials}
              </div>
              <p className="text-sm text-surface-700">{currentFullName || '—'}</p>
            </div>
          )}
        </SettingRow>

        {/* Email — always read-only; self-service change is out of scope */}
        <SettingRow label={t('profile.email')}>
          <div className="flex flex-wrap items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-surface-400" strokeWidth={1.8} />
            <p className="text-sm text-surface-700">{user?.email || '—'}</p>
            {user?.emailVerified ? (
              <span className="inline-flex items-center gap-1 text-xs text-green-600">
                <CheckCircle className="h-3.5 w-3.5" strokeWidth={2} />
                {t('profile.emailVerified')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                <AlertCircle className="h-3.5 w-3.5" strokeWidth={2} />
                {t('profile.emailNotVerified')}
              </span>
            )}
            {user && !user.emailVerified && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={resendState === 'loading' || resendState === 'sent'}
                onClick={async () => {
                  setResendState('loading');
                  try {
                    await fetchApi('/auth/resend-verify', { method: 'POST' });
                    setResendState('sent');
                  } catch {
                    setResendState('error');
                  }
                }}
              >
                {resendState === 'loading'
                  ? t('profile.sending')
                  : resendState === 'sent'
                    ? t('profile.emailSent')
                    : resendState === 'error'
                      ? t('profile.resendError')
                      : t('profile.resendVerify')}
              </Button>
            )}
          </div>
        </SettingRow>
      </CardContent>
    </Card>
  );
}
